// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMBusyIndicatorService, LMContextService, LMLoggerService } from '@services/index';
import { LMErrorUtilService, LMUrlUtilService } from '@services/utils';
import { NavigationExtras, Router } from '@angular/router';
import {
  AppConstants,
  FormRequestType,
  LMContextUtilService,
  LMFormInvokeUrlPostContent,
  LMFormInvokeUrlResponse,
  LMGlobalUtilService
} from '@utils/index';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, first } from 'rxjs/operators';

export interface LMNavigationOptions extends NavigationExtras {
  showAsPdf?: boolean;
  openNewTab?: boolean;
}

@Injectable()
export class LMNavigationService {

  constructor(
    private http: HttpClient,
    private router: Router,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmContextService: LMContextService,
    private lmContextUtilService: LMContextUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmErrorUtilService: LMErrorUtilService,
  ) {}

  public getActionNavigationUrl(navigationUrl: string): Observable<any> {  // todo ppatton type the Observable response
    // todo combine all "getRedirectUrl" functions into a single service.
    // todo should .first() be used here or where the implementing code subscribes?
    // any subscription to this should be called once and then closed
    return this.http.get(navigationUrl).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  private getFormInvokeUrl(formInvokeUrl: string, postContent: LMFormInvokeUrlPostContent) {
    return this.http.post(formInvokeUrl, postContent).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  public runCreateActionNavigation(createActionUrl: string) {
    this.getActionNavigationUrl(createActionUrl).pipe(first()).subscribe(response => {
      // --------------------------------------------------------------------------------
      // todo based on the response may need to load the url in a dialog instead
      // of routing to a new component.
      // --------------------------------------------------------------------------------

      if (response.mfaUrl) {
        this.lmGlobalUtilService.navigate(response.mfaUrl);
        return;
      }

      const relativeUrl = this.lmUrlUtilService.toRelativeUrl(response.redirectUrl, true, true);

      // ---------------------------------------------------------------------------
      // todo ppatton if navigating to a new route (form) then apply paging
      // information to the url so the form next/prev fun ctionality works.
      // ---------------------------------------------------------------------------
      this.router.navigateByUrl(relativeUrl);
    });
  }

  /**
   *
   * @param formInvokeUrl
   * @param formInvokeName
   * @param dataView
   */
  public runFormInvoke(navigationUrl: string, formInvokeName: string, dataView: LMDataView) {

    // No busy indicator in service. The components will show / hide the busy indicator
    // this.lmBusyIndicatorService.showBusyIndicator();

    // place into a timeout so the busyIndicator has a chance to show immediately.
    setTimeout(() => {
      const postContent: LMFormInvokeUrlPostContent = {
        requestType:    FormRequestType.FormInvoke,
        formInvokeName,
        dataView
      };

      this.getFormInvokeUrl(navigationUrl, postContent).subscribe((response: LMFormInvokeUrlResponse) => {
        // --------------------------------------------------------------------------------
        // todo based on the response may need to load the url in a dialog instead
        // of routing to a new component.
        // --------------------------------------------------------------------------------
        if (response.redirectUrl) {
          this.lmGlobalUtilService.navigateByUrl(response.redirectUrl);

        } else if (response.status && response.status === AppConstants.VIEW_EXCEPTION) {
          this.lmErrorUtilService.handleError(response.message, response.details, response.status);
        }
      });
    });
  }

  public postProcessHttpUrl(url: string, openNewTab: boolean, extras?: NavigationExtras) {
    const option: string = (openNewTab) ? 'external' : '';
    if (url) {

      // When getting a http url/linkback that returns a navigation, we need to handle
      // by calling runNavigation instead of navigate. This will prevent the navigation
      // url from replacing the current lmclient route.
      // see LMCLIENT-35609.
      let resource: KeyedResource;
      try {
        //getRouteResource will throw an error if the url cannot be parsed.
        resource = this.lmUrlUtilService.getRouteResource(url);
      } catch (e) {
        //noop
      }

      if (resource?.resourceType === 'navigation') {
        this.runNavigation(url, undefined, { openNewTab });
      } else if (this.lmUrlUtilService.urlHostMatchesWindowLocationHost(url)
          && (resource?.resourceType === 'page'
          || resource?.resourceType === 'list'
          || resource?.resourceType === 'form'
          || resource?.resourceType === 'menu')) {
        // ------------------------------------------------------------
        // LMWEB-20892 Current URL does not have Angular Route Defined.
        // This could still happen if the non lpl screen url has a
        // resourceType that we recognize as one of our resourceTypes.
        // If that happens we'll need a better way to determine if
        // the url is one of ours or not.
        // ------------------------------------------------------------

        // ----------------------------------------------------------------
        // https://inforwiki.atlassian.net/browse/LMWEB-21236
        // In some cases the url hosts may not match. This happens when
        // going to the Jobs standalone app as an employee, when clicking on
        // the sign-in link it attempts to go to another server.
        // If this is the case then we need to call navigate() instead of
        // navigateByUrl() (which assumes the url host is the same as the
        // window.location.host)
        // ----------------------------------------------------------------
        url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
        this.lmGlobalUtilService.navigateByUrl(url, extras);
      } else {
        this.lmGlobalUtilService.navigate(url, null, option);
      }
    }
  }

  public runNavigation(navigationUrl: string, pagingInfo?: LMPagingInfo, options?: LMNavigationOptions) {

    // No busy indicator in service. The components will show / hide the busy indicator
    // Causing issue in form load since the busy indicator is dismissed prematurely in form spec loaded
    // this.lmBusyIndicatorService.showBusyIndicator();

    // place into a timeout so the busyIndicator has a chance to show immediately.
    setTimeout(() => {
      navigationUrl = this.lmUrlUtilService.updateUrlParams(navigationUrl, { output: 'spec' });
      this.getActionNavigationUrl(navigationUrl).subscribe(response => {
        if (response.messageId && !response.redirectUrl) {
          // Common Inbox Integration - Direct route to open notification may return error messages.
          this.lmErrorUtilService.handleError(Soho.Locale.translate(response.messageId));
          return;
        } else if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
          this.lmErrorUtilService.handleError(response.message, response.details, response.status, response.statusText);
          return;
        }

        if (response.mfaUrl) {
          this.lmGlobalUtilService.navigate(response.mfaUrl);
          return;
        }
        let redirectUrl = (this.lmContextService.inMingleWidget())
          ? response.redirectUrl : this.lmUrlUtilService.toRelativeUrl(response.redirectUrl, true, true);
        if (pagingInfo) {
          // ---------------------------------------------------------------------------
          // if navigating to a new route (form) then apply paging
          // information to the url so the form next/prev functionality works.
          // ---------------------------------------------------------------------------
          const delimiter = redirectUrl.indexOf('?') === -1 ? '?' : '&';
          redirectUrl += delimiter + Object.entries(pagingInfo).map(pair => LMUrlUtilService.toUrlParam(pair, true)).join('&');
        }

        // For now, lets not retain page state if we run a navigation.
        // Need the check below because the open action on a list uses this api
        if (response.hasPositionTo) {
          this.lmContextService.setClearNextPageState(true, false);
        }

        // --------------------------------------------------------------------------------
        // todo based on the response may need to load the url in a dialog instead
        // of routing to a new component.
        // --------------------------------------------------------------------------------
        if (options && options.showAsPdf) {
          this.lmUrlUtilService.openFile(redirectUrl);
        } else if (redirectUrl.indexOf('output=pdf') > -1) {
          this.lmGlobalUtilService.navigate(redirectUrl, null, 'external');
        } else if (options && options.openNewTab) {
          redirectUrl = this.lmUrlUtilService.encodeParens(redirectUrl);
          this.lmUrlUtilService.openExternalUrl(redirectUrl);
        } else if (this.lmContextService.inMingleWidget()) {
          this.lmContextUtilService.sendDrillbackMessage(redirectUrl);
        }  else if (redirectUrl.indexOf('/document/') > -1) {
          this.lmGlobalUtilService.navigate(redirectUrl);
        } else {
          this.lmGlobalUtilService.navigateByUrl(redirectUrl, options);
        }
        // this.lmBusyIndicatorService.hideBusyIndicator();
      });
    });
  }

  public runNotificationAction(navigationUrl: string, options?: LMNavigationOptions): Observable<boolean> {
    navigationUrl = this.lmUrlUtilService.updateUrlParams(navigationUrl, { output: 'spec' });
    return new Observable<boolean>(observer => {
      this.getActionNavigationUrl(navigationUrl).subscribe(response => {
        if (response.messageId && !response.redirectUrl) {
          // Common Inbox Integration - Direct route to open notification may return error messages.
          this.lmErrorUtilService.handleError(Soho.Locale.translate(response.messageId));
          observer.next(true);
          observer.complete();
          return;
        } else if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
          this.lmErrorUtilService.handleError(response.message, response.details, response.status, response.statusText);
          observer.next(true);
          observer.complete();
          return;
        }
        const redirectUrl = (this.lmContextService.inMingleWidget())
          ? response.redirectUrl : this.lmUrlUtilService.toRelativeUrl(response.redirectUrl, true, true);
        if (redirectUrl.indexOf('output=pdf') > -1) {
          // pdf
          this.lmGlobalUtilService.navigate(redirectUrl, null, 'external');
          observer.next(true);
        } else if (redirectUrl.indexOf('/document/') > -1) {
          // csv - no need to add it to history.
          this.lmGlobalUtilService.navigate(redirectUrl, null, 'replace');
          observer.next(true);
        } else {
          this.lmGlobalUtilService.navigateByUrl(redirectUrl, options);
          observer.next(false);
        }
        observer.complete();
      });
    });
  }
}
